import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
	AgentInfo,
	AgentInfoChild,
	Corner,
	FooterWrapper,
	FrameGrid,
	Item,
	LaunchButton,
	Main,
} from '../styles/commonStyles';
import {
	BodyWrapper,
	Box1,
	Box2,
	LeftPadding,
	RightBottomBox,
	RightPadding,
	RightTopBox,
} from '../styles/layoutStyles';
import Footer from 'components/FooterBackground';
import LoginModal from 'components/LoginModal';

interface LoginViewProps {
	loading: boolean;
}

export const LoginView: React.FC<LoginViewProps> = ({ loading }) => {
	const [loginModalOpen, setLoginModalOpen] = useState(false);
	const theme = useTheme();

	const RenderImage = useCallback(() => {
		return (
			<Box
				component="img"
				src={
					theme.palette.mode === 'light'
						? '/images/bg-screen-light.png'
						: '/images/bg-screen.png'
				}
				alt="Dudu"
				sx={{
					zIndex: 10,
					height: '100%',
					width: '100%',
					objectFit: 'cover',
					top: 0,
					left: 0,
					opacity: 0.5,
				}}
			/>
		);
	}, [theme.palette.mode]);

	const RenderLogoImage = useCallback(() => {
		return (
			<img
				src={'/images/Globe.png'}
				height={48}
				width={48}
				alt={'Aptos'}
				style={{
					marginLeft: 'auto',
				}}
			/>
		);
	}, []);

	return (
		<Main>
			<BodyWrapper>
				<LeftPadding />
				<Box1>
					<AgentInfo
						sx={{
							paddingBlock: { xs: '16px', sm: '32px' },
							paddingInline: { xs: '16px', sm: '32px' },
						}}
					>
						<AgentInfoChild>
							<Corner style={{ top: 0, left: 0 }} />
							<Corner
								style={{
									top: 0,
									right: 0,
									transform: 'scaleX(-1)',
								}}
							/>
							<Corner
								style={{
									bottom: 0,
									left: 0,
									transform: 'scaleY(-1)',
								}}
							/>
							<Corner
								style={{
									bottom: 0,
									right: 0,
									transform: 'scale(-1, -1)',
								}}
							/>
							<Item>
								<Typography
									fontWeight={500}
									variant="h3"
									sx={{
										color: 'var(--atlas-accent-primary-alt)',
										fontSize: {
											xs: '16px',
											sm: '24px',
											md: '32px',
										},
										fontWeight: 600,
										fontFamily: 'Oxanium',
										lineHeight: 1.2,
									}}
								>
									AI AGENT
								</Typography>
								<Typography
									color="textPrimary"
									variant="h1"
									fontFamily={'AmigaForever'}
									sx={{
										fontSize: {
											xs: '32px',
											sm: '70px',
											md: '100px',
										},
										fontWeight: 500,
										fontFamily: 'AmigaForever',
										mt: { xs: 1, sm: 4, md: 6 },
										lineHeight: 1.1,
									}}
								>
									ATLAS
								</Typography>
								<Box height={{ xs: 4, sm: 16, md: 24 }} />
								<Typography
									variant="body2"
									fontWeight={400}
									sx={{
										color: 'var(--atlas-text-placeholder)',
										width: {
											xs: '100%',
											sm: '90%',
											md: '80%',
										},
										fontSize: {
											xs: '14px',
											sm: '16px',
											md: '16px',
										},
										fontWeight: 500,
										fontFamily: 'Roboto Mono',
										lineHeight: { xs: '1.4', sm: '1.5' },
										mt: { xs: 0.5, sm: 1 },
										mb: { xs: 2, sm: 3 },
									}}
								>
									Stablecoin yield maximiser for Aptos.
									<br />
									Atlas uses auto-rebalancing and intelligent
									workflows to make the most of your stables.
								</Typography>

								<LaunchButton
									onClick={() => setLoginModalOpen(true)}
									sx={{
										fontFamily: 'AmigaForever',
										fontSize: {
											xs: '12px',
											sm: '13px',
											md: '14px',
										},
										fontWeight: 500,
										textTransform: 'uppercase',
										backgroundColor:
											'var(--atlas-bg-light-strong)',
										paddingBlock: {
											xs: '12px',
											sm: '11px',
											md: '12px',
										},
										paddingInline: {
											xs: '24px',
											sm: '50px',
											md: '70px',
										},
										color: 'var(--atlas-text-navy)',
										width: {
											xs: '90%',
											sm: 'auto',
											md: '60%',
										},
										'&:hover': {
											backgroundColor:
												'var(--atlas-bg-light-translucent)',
										},
									}}
								>
									Connect Wallet
								</LaunchButton>
							</Item>
						</AgentInfoChild>
					</AgentInfo>
					<FrameGrid>
						<Box
							sx={{
								padding: {
									xs: '8px',
									sm: '30px',
									md: '60px',
								},
								position: 'absolute',
								top: { xs: 8, sm: 40, md: 80 },
								right: { xs: 8, sm: 40, md: 80 },
								zIndex: 10,
								flexDirection: 'column',
								alignItems: 'flex-end',
								justifyContent: 'center',
								gap: '8px',
								display: { xs: 'none', sm: 'flex' },
							}}
						>
							<Typography
								variant="caption"
								sx={{
									fontSize: {
										xs: '14px',
										sm: '14px',
										md: '16px',
									},
									fontFamily: 'Roboto Mono',
									color: 'var(--atlas-text-primary)',
									textAlign: 'right',
								}}
							>
								Beta mode
								<br />
							</Typography>

							<RenderLogoImage />
						</Box>
					</FrameGrid>
				</Box1>
				<Box2>
					<RightTopBox />

					<RightBottomBox>
						<RenderImage />
					</RightBottomBox>

					<Box
						component="img"
						src="/images/atlas-logo-full.png"
						alt="Atlas"
						sx={{
							height: { xs: '40vh', sm: '50vh', md: '80vh' },
							alignSelf: 'center',
							position: 'absolute',
							bottom: 0,
							left: {
								xs: 'calc(50% - 20vh)',
								sm: 'calc(50% - 25vh)',
								md: '-35%',
							},
						}}
					/>
				</Box2>
				<RightPadding />
				<FooterWrapper>
					<Footer />
				</FooterWrapper>
			</BodyWrapper>

			{/* Login Modal*/}
			<LoginModal
				open={loginModalOpen}
				onClose={() => setLoginModalOpen(false)}
			/>
		</Main>
	);
};
